import React from 'react';

import { Box, Divider, Paper, Typography, useTheme } from '@material-ui/core';

import { AppButton } from '@vyce/core/src/components/AppButton';
import { useBooleanState } from '@vyce/core/src/hooks';
import { getColor } from '@vyce/core/src/theme/styles';
import { DRC_APPLIES_EXPLANATION } from '@vyce/core/src/constants';
import { getUKFormattedDate } from '@vyce/core/src/utils/dates';

import profileStyles from '../../styles';
import { useStyles } from './styles';
import { DRCProfileState } from './hooks/useDRCProfile';
import { DRCStatusChip } from './components/DRCStatusChip';
import { DRCBlockersList } from './components/DRCBlockersList';
import { VATAndCISSection } from './components/VATAndCISSection';
import { EndUserStatusControl } from './components/EndUserStatusControl';
import { DRCPeriodSection } from './components/DRCPeriodSection';
import { IR35Section } from './components/IR35Section';
import { DRCEventsDialog } from './components/DRCEventsDialog';

interface Props {
  /**
   * The DRC profile is keyed by user, not by employee — `employee.user_id`, not `employee.uuid`.
   */
  userId?: string;
  /** The HMRC VAT check is keyed by the pay employee — `employee.uuid`. */
  employeeId?: string;
  /**
   * Owned by the Employment tab, because the Companies House check lives in its Limited
   * Company block and has to share this profile with the panel.
   */
  drc: DRCProfileState;
}

/**
 * VAT Domestic Reverse Charge controls for the Employment tab. Legend only: every route behind
 * this panel is mounted on admin/v1.
 *
 * The panel keeps its own state and fires its own requests rather than joining the tab's shared
 * form, which diffs fields shallowly against the employee record.
 */
export const DRCPanel: React.FC<Props> = ({ userId, employeeId, drc }) => {
  const classes = useStyles();
  const profileClasses = profileStyles();
  const theme = useTheme();
  const colors = getColor(theme.palette.type === 'dark');
  const [isHistoryOpen, openHistory, closeHistory] = useBooleanState(false);

  const {
    profile,
    status,
    loading,
    saving,
    vatChecking,
    saveProfile,
    setEndUserStatus,
    recordIR35,
    setPeriod,
    verifyVat,
  } = drc;

  if (!userId) {
    return null;
  }

  return (
    <>
      <Box marginRight={1} marginBottom={2} display="flex" alignItems="center" gridGap={12} flexWrap="wrap">
        <Typography className={profileClasses.tabTitle}>VAT &amp; reverse charge</Typography>
        <DRCStatusChip status={status} />
        <Box marginLeft="auto">
          <AppButton variant="outlined" color="primary" size="small" onClick={openHistory}>
            History
          </AppButton>
        </Box>
      </Box>

      <Paper className={profileClasses.formPaper} variant="outlined">
        {loading ? (
          <Typography variant="body2" color="textSecondary">
            Loading…
          </Typography>
        ) : (
          <Box display="flex" flexDirection="column" gridGap={20}>
            {/* A worker nobody has set up answers 200 with a null profile — an empty state, not an error. */}
            {!profile && (
              <Typography variant="body2" color="textSecondary">
                Nothing has been set up for this worker yet. Fill in the details below to assess whether the
                domestic reverse charge applies.
              </Typography>
            )}

            <VATAndCISSection
              profile={profile}
              status={status}
              saving={saving}
              checking={vatChecking}
              onSave={saveProfile}
              onVerify={employeeId ? vatNumber => verifyVat(employeeId, vatNumber) : undefined}
            />

            <Divider />

            <EndUserStatusControl profile={profile} disabled={saving} onChange={setEndUserStatus} />

            <Divider />

            <DRCPeriodSection profile={profile} saving={saving} onSave={setPeriod} />

            <Divider />

            <IR35Section profile={profile} saving={saving} onSave={recordIR35} />

            <DRCBlockersList status={status} />

            {status?.applies && (
              <Box
                className={classes.appliesBanner}
                style={{ background: colors.pillOnBG, color: colors.pillOnText }}>
                <Box>
                  <Typography variant="subtitle2" style={{ color: colors.pillOnText }}>
                    {`Reverse charge applies from ${getUKFormattedDate(status.effective_from)}`}
                  </Typography>
                  <Typography variant="body2" style={{ color: colors.pillOnText }}>
                    {DRC_APPLIES_EXPLANATION}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Paper>

      <DRCEventsDialog open={isHistoryOpen} userId={userId} handleClose={closeHistory} />
    </>
  );
};
